// Real HTTP client for the demo cloud (cloud/api/openapi.yaml). Implements the
// same ApiClient surface as the in-memory mock, so it is a drop-in swap — see
// ./index.ts, which picks this when VITE_API_BASE is set.
//
// Auth: the client holds a bearer token, and the ONLY way to get one is an
// explicit sign-in through the Login screen. No token ⇒ the request fails 401 and
// the shell drops to Login.
//
// This client used to lazily auto-log-in with hardcoded demo credentials so that
// deep-links "just worked". That stopped being possible once the demo personas
// became CONTROLLED_EMAILS (cloud/src/handler.ts) with real, randomly-generated
// passwords: the only way to keep auto-login working would be to ship that
// password in the bundle, i.e. publish it. Worse, the dead auto-login was firing a
// FAILING sign-in on every page load, and the server counts sign-in failures
// against the source IP as well as the email — so a handful of visits from one IP
// locked that IP out of the demo. Do not reintroduce it.
import type { ApiClient, LoginResult } from './index'
import type {
  Adm, AdminAccountSummary, AdminAuditEvent, AdminDeviceSummary, AdminSiteSummary, Authority, BackstopStatus, Battery, BatteryPack, Behaviour, CommandsStatus, CommissioningStep, ComplianceRecord, Consent, ConsentLogEntry, DerStatusStatus, Device, DeviceKind,
  CommandView, DeviceCommandRequest, DeviceCommandSubmission, EconomicSettings, EconomicSettingsPatch,
  DiagnosticRegister, DocContent, DocMeta, EnergyPeriod, EnergySummary, RecordedEnergyDay, FirmwareCampaignAbort, FirmwareCampaignView, FirmwareInfo, FleetAssociation, FleetSite, GridJurisdiction, HandoverResult, HwsDeclaration,
  DemandCharge, InstallerApplication, InstallerOrg, InstallerOrganisationView, Inverter, InventoryKind, InventoryUnit, InventoryValidation, CorrectionResult, DeclarationResult, Mode, AppNotification, NotificationPrefs, OrgAuditEvent, OrgMembership, OrgRole, PairingStatus, PowerFlow, Site, SupportOverview, Tariff, TariffPeriod, TariffTier, TariffType, TelemetryStatus, TouSlot, UiPrefs, User, Warranty, Weather,
  UpdateJobView, UpdatePlan,
  VppDispatchRequest, VppEvent, VppPartnerView, VppSiteStatus, VppTokenResponse, VppWebhookDelivery,
} from './types'
import type { BleClient } from './bleClient'
import { createBleClient, createHttpBridgeTransport } from './bleClient'
import { networkError, timeoutError } from '../lib/apiErrors'

const TOKEN_KEY = 'bess_api_token'
// The acting Installer Organisation (APP-ACC-08L). A CLIENT-side choice, stored
// like the token and sent on every organisation-scoped request — see the
// acting-organisation section of ApiClient for why it is not a server resource.
const ACTING_ORG_KEY = 'bess_acting_organisation'
// Flags the retired auto-login wrote (see header). Every browser that ran a build
// from before it was removed still has them, and nothing reads them any more —
// purge once at construction so they don't linger as a trap for the next reader.
const RETIRED_KEYS = ['bess_session_explicit', 'bess_signed_out']
// The MOCK VPP partner's client credentials (ICD-API-02). Published on purpose: this
// is a demo sandbox partner (CLD-VPP-06), and pretending the secret is a secret would
// be theatre. A real partner's credential would come from an IdP, per-partner.
const PARTNER_CLIENT_ID = 'origin-loop'
const PARTNER_CLIENT_SECRET = 'demo-partner-secret'
const POLL_MS = 2000

/**
 * EVERY REQUEST IS BOUNDED. Without this a socket that opens and never answers
 * leaves the promise pending forever, and a screen keyed off an in-flight flag —
 * InstallerInventory's Withdraw/Correct, InstallerAdd's enroll, every button on
 * OnsiteCommissioning — stays at "Submitting…" with its controls disabled and no
 * cancel, because the `finally` that clears the flag never runs.
 *
 * A timeout is NOT a refusal and this client never reports it as one: it raises
 * its own `code: 'timeout'` error whose sentence says the outcome is UNKNOWN
 * (`lib/apiErrors`), so a caller can re-read rather than assert that nothing
 * happened. The SSE stream is deliberately exempt — a long-lived stream that
 * answers rarely is working as designed.
 */
export const REQUEST_TIMEOUT_MS = 20_000

/** `AbortSignal.timeout` where the runtime has it (every browser target and Node
 *  ≥ 17.3). Older/stubbed environments simply get no deadline rather than a crash. */
function deadline(ms: number): AbortSignal | undefined {
  return typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function'
    ? AbortSignal.timeout(ms)
    : undefined
}

/** Classify a `fetch` rejection: an abort from OUR deadline is a timeout; anything
 *  else that never reached a response is a transport failure. Neither is a status. */
function asRequestFailure(e: unknown, timeoutMs: number): Error {
  const name = (e as { name?: string } | null)?.name
  if (name === 'TimeoutError' || name === 'AbortError') return timeoutError(timeoutMs)
  if (e instanceof TypeError) return networkError()
  return e instanceof Error ? e : networkError()
}

/** `fetch` with a deadline and classified failures. The single door every
 *  non-stream request in this client goes through. */
async function fetchWithTimeout(input: string, init: RequestInit, timeoutMs = REQUEST_TIMEOUT_MS): Promise<Response> {
  try {
    return await fetch(input, { ...init, signal: deadline(timeoutMs) })
  } catch (e) {
    throw asRequestFailure(e, timeoutMs)
  }
}

/** A 401-equivalent for "there is no session", raised without a round-trip. */
function authError(): Error {
  const e = new Error('401 signed out') as Error & { status: number }
  e.status = 401
  return e
}
const isUnauthorized = (e: unknown): boolean => (e as { status?: number })?.status === 401

interface LoginResponse {
  token: string
  expiresAt?: string
  user: User
}
/** POST /auth/login returns this instead of a token when the account has opt-in
 *  2FA (APP-ACC-07) — the caller completes it via POST /auth/2fa. */
interface ChallengeResponse {
  challengeRequired: true
  email: string
  challengeToken: string
  enrollmentRequired: boolean
  enrollmentSecret?: string
}

export class HttpApiClient implements ApiClient {
  private readonly base: string
  private readonly streamBase: string | null
  private token: string | null
  private user: User | null = null
  // Once the SSE stream has failed this many times in a session, stay on polling
  // (the documented fallback) for the rest of it — don't thrash reconnects.
  private streamGiveUp = false
  // The VPP PARTNER bearer (ICD-API-02), kept strictly apart from the session token:
  // the cloud refuses a partner credential anywhere but /partner/*, and a homeowner
  // session on /partner/*. In-memory only — a partner grant is not the user's session
  // and has no business surviving in localStorage next to it.
  private partnerToken: string | null = null
  // The onsite BLE client, created on first use and kept: the session lives on it
  // (see ApiClient.ble).
  private bleClient: BleClient | null = null

  constructor(base: string, streamBase?: string) {
    this.base = base.replace(/\/+$/, '')
    this.streamBase = streamBase ? streamBase.replace(/\/+$/, '') : null
    this.token = localStorage.getItem(TOKEN_KEY)
    for (const key of RETIRED_KEYS) localStorage.removeItem(key)
  }

  // ---- auth plumbing ----

  /** Establish a session. Every session comes from an explicit sign-in. */
  private setSession(token: string, user: User): void {
    this.token = token
    this.user = user
    localStorage.setItem(TOKEN_KEY, token)
  }

  /** Drop the session — a deliberate sign-out, or one the server has rejected. */
  private clearSession(): void {
    this.token = null
    this.user = null
    localStorage.removeItem(TOKEN_KEY)
    // The acting organisation belongs to the signed-in practitioner, not to the
    // browser: the next account must choose its own (APP-ACC-08L).
    localStorage.removeItem(ACTING_ORG_KEY)
  }

  /** POST /auth/login — returns a token (signed in) OR a 2FA challenge. */
  private async postLogin(body: Record<string, string>): Promise<LoginResponse | ChallengeResponse> {
    const res = await fetchWithTimeout(`${this.base}/auth/login`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!res.ok) throw await toError(res)
    return (await res.json()) as LoginResponse | ChallengeResponse
  }

  private applyToken(data: LoginResponse): void {
    this.setSession(data.token, data.user)
  }

  /** Require a session. Never establishes one: only an explicit sign-in does that,
   *  so a caller with no token fails 401 here and the shell returns to Login. */
  private requireAuth(): void {
    if (!this.token) throw authError()
  }

  private async raw(method: string, path: string, body?: unknown): Promise<Response> {
    const headers: Record<string, string> = {}
    if (this.token) headers.authorization = `Bearer ${this.token}`
    if (body !== undefined) headers['content-type'] = 'application/json'
    return fetchWithTimeout(`${this.base}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
  }

  /** Authenticated request. A rejected token ends the session — there is no
   *  identity to silently fall back to.
   *  GETs retry once on 502/503/504 — the demo account has a small Lambda
   *  concurrency limit, so a burst (or an SSE connection holding a slot) can
   *  throttle a read; without this a one-shot screen fetch stays blank forever.
   *  Only GETs: they are safe to repeat, mutations are not. */
  private async req<T>(method: string, path: string, body?: unknown): Promise<T> {
    this.requireAuth()
    let res = await this.raw(method, path, body)
    if (res.status === 401) {
      // The session ended (expired/revoked). The shell sees the 401
      // (getCurrentUser → null) and returns to Login.
      this.clearSession()
      throw await toError(res)
    }
    if (method === 'GET' && (res.status === 502 || res.status === 503 || res.status === 504)) {
      await new Promise((r) => setTimeout(r, 600))
      res = await this.raw(method, path, body)
    }
    if (!res.ok) throw await toError(res)
    if (res.status === 204) return undefined as T
    return (await res.json()) as T
  }

  // ---- ApiClient surface ----

  async login(email: string, password: string): Promise<LoginResult> {
    const data = await this.postLogin({ email, password })
    if ('challengeRequired' in data) return data
    this.applyToken(data)
    return { user: data.user }
  }

  /** End the session (APP-ACC-06) — so "sign out" actually ends the session
   *  instead of leaving a privilege-bearing token in place. */
  async signOut(): Promise<void> {
    this.clearSession()
  }

  /** Complete a 2FA challenge (APP-ACC-07): POST /auth/2fa → token. */
  async verifyTwoFactor(email: string, code: string, challengeToken: string): Promise<User> {
    const res = await fetchWithTimeout(`${this.base}/auth/2fa`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email, code, challengeToken }),
    })
    if (!res.ok) throw await toError(res)
    const data = (await res.json()) as LoginResponse
    this.applyToken(data)
    return data.user
  }

  getTwoFactorSetup(): Promise<{ secret: string; uri: string }> {
    return this.req('GET', '/security/2fa/setup')
  }

  setTwoFactor(enabled: boolean, code?: string): Promise<User> {
    return this.req('PUT', '/security/2fa', { enabled, ...(code ? { code } : {}) })
  }

  async signup(name: string, email: string, password: string): Promise<User> {
    // Self-serve, un-gated account creation (APP-ACC-01). Demo-equivalent to login
    // (per-email world); the display name is persisted on the profile server-side.
    // A new account never has 2FA on, so this always returns a token.
    const data = await this.postLogin({ email, password, name: name.trim() })
    if ('challengeRequired' in data) throw new Error('Unexpected 2FA challenge during sign-up')
    this.applyToken(data)
    return data.user
  }

  async getCurrentUser(): Promise<User | null> {
    try {
      const user = await this.req<User>('GET', '/auth/me')
      this.user = user
      return user
    } catch (e) {
      // Genuinely unauthenticated (signed out / session ended) → no user. A
      // transient failure (5xx / network) rethrows so the caller keeps the
      // last-known identity instead of bouncing the user out on a blip.
      if (isUnauthorized(e)) return null
      throw e
    }
  }

  // ---- installer identity (APP-ACC-09 / CLD-PRT-09) ----

  applyInstaller(org: InstallerOrg): Promise<User> {
    return this.req('POST', '/installer/apply', org)
  }

  async getInstallerApplication(): Promise<InstallerApplication | null> {
    const r = await this.req<{ application: InstallerApplication | null }>('GET', '/installer/application')
    return r.application ?? null
  }

  adminListInstallerApplications(): Promise<InstallerApplication[]> {
    return this.req('GET', '/admin/installer-applications')
  }

  adminDecideInstallerApplication(userId: string, approve: boolean): Promise<InstallerApplication> {
    return this.req('POST', '/admin/installer-applications/decide', { userId, approve })
  }

  adminListAccounts(query = ''): Promise<AdminAccountSummary[]> {
    return this.req('GET', `/admin/accounts?query=${encodeURIComponent(query)}`)
  }
  adminListSites(query = ''): Promise<AdminSiteSummary[]> {
    return this.req('GET', `/admin/sites?query=${encodeURIComponent(query)}`)
  }
  adminListDevices(query = ''): Promise<AdminDeviceSummary[]> {
    return this.req('GET', `/admin/devices?query=${encodeURIComponent(query)}`)
  }
  adminListAudit(): Promise<AdminAuditEvent[]> {
    return this.req('GET', '/admin/audit')
  }
  adminSetInstallerCredential(accountId: string, status: 'approved' | 'suspended' | 'revoked', reason: string, ticket: string): Promise<{ account: AdminAccountSummary; audit: AdminAuditEvent }> {
    return this.req('POST', '/admin/installer-credentials', { accountId, status, reason, ticket, idempotencyKey: crypto.randomUUID() })
  }
  adminSetAccountLock(accountId: string, locked: boolean, reason: string, ticket: string): Promise<{ account: AdminAccountSummary; audit: AdminAuditEvent }> {
    return this.req('POST', '/admin/account-lock', { accountId, locked, reason, ticket, idempotencyKey: crypto.randomUUID() })
  }

  getSite(): Promise<Site> {
    return this.req('GET', '/site')
  }

  listResidences(): Promise<import('./types').ResidenceList> {
    return this.req('GET', '/residences')
  }

  async switchResidence(siteId: string): Promise<import('./types').ResidenceSummary> {
    const data = await this.req<{
      token: string
      expiresAt: string
      activeSiteId: string
      residence: import('./types').ResidenceSummary
    }>('POST', '/session/residence', { siteId })
    // Session siteId lives on the bearer — replace it so every subsequent call
    // addresses the selected Residence (Stage 4.1 multi-Residence shell).
    this.token = data.token
    localStorage.setItem('bess_api_token', data.token)
    return data.residence
  }

  resetSiteData(): Promise<{ reset: boolean; deletedItems: number }> {
    return this.req('POST', '/site/reset')
  }

  getPowerFlow(): Promise<PowerFlow> {
    return this.req('GET', '/power-flow')
  }

  /** Live power-flow. Prefers the SSE stream (one server push every ~2 s, no
   *  request-per-tick); falls back to polling `/power-flow` when no stream origin
   *  is configured or the stream has failed 3× this session. Returns an unsubscribe
   *  that stops whichever loop is running. */
  subscribeLive(cb: (pf: PowerFlow) => void): () => void {
    let stopped = false
    let pollTimer: ReturnType<typeof setInterval> | null = null
    const controller = new AbortController()

    const startPolling = () => {
      if (pollTimer !== null) return
      const tick = async () => {
        try {
          const pf = await this.getPowerFlow()
          if (!stopped) cb(pf)
        } catch {
          /* transient — the next tick retries */
        }
      }
      void tick()
      pollTimer = setInterval(tick, POLL_MS)
    }

    // The SSE loop: connect, emit each frame, reconnect on clean end; after 3
    // consecutive failures give up and poll for the rest of the session.
    const runStream = async () => {
      let failures = 0
      while (!stopped && this.streamBase) {
        // No session ⇒ nothing to stream. Checked OUTSIDE the try on purpose: this
        // is not a transport blip, it cannot self-heal from inside the loop, and
        // letting it reach the catch below would burn the give-up budget on retries
        // that must fail — permanently latching streamGiveUp on this client (it is a
        // module singleton), so even a later sign-in would silently never get SSE
        // back. A signed-in caller re-subscribes on mount and starts a fresh loop.
        if (!this.token) return
        try {
          // The stream is fronted by CloudFront (OAC), which owns the Authorization
          // header for its SigV4 signature — so the session token rides X-Bess-Token
          // (forwarded by the /stream/* origin-request policy to the Lambda).
          const connect = () => fetch(this.streamBase as string, {
            headers: { 'x-bess-token': this.token as string },
            signal: controller.signal,
          })
          const res = await connect()
          if (res.status === 401) {
            // Session ended — stop streaming (mirror req()).
            this.clearSession()
            return
          }
          if (!res.ok || !res.body) throw new Error(`stream ${res.status}`)
          const reader = res.body.getReader()
          const decoder = new TextDecoder()
          let buffer = ''
          for (;;) {
            const { value, done } = await reader.read()
            if (done || stopped) break
            buffer += decoder.decode(value, { stream: true })
            const parsed = parseSseChunk(buffer)
            buffer = parsed.rest
            for (const f of parsed.frames) {
              failures = 0 // any frame proves the stream works
              if (!stopped) cb(f as PowerFlow)
            }
          }
          // Clean end (300 s server cap) → reconnect immediately.
        } catch (err) {
          if (stopped) return
          failures++
          // NOT A BARE `catch {}` ANY MORE. The live-data path degrades to
          // polling and the UI stays healthy-looking, which is correct for the
          // homeowner and terrible for whoever has to work out why the stream is
          // dead. The specific case this hides is a CloudFront 403 — the whole
          // of pass 1 of the two-pass deploy, where `streamDistributionArn` is
          // unset so the OAC grant does not exist yet — and it looked identical
          // to a transient network blip. The stream OAC and the grant can be
          // provisioned and paid for while the app never successfully calls
          // them, and nothing anywhere says so.
          console.warn(
            `[bess] power-flow stream attempt ${failures} failed: ${err instanceof Error ? err.message : String(err)}`,
          )
          if (failures >= 3) {
            this.streamGiveUp = true
            console.warn(
              '[bess] giving up on the power-flow stream; falling back to polling. ' +
                'A persistent "stream 403" here means the CloudFront OAC grant is missing — ' +
                'set streamDistributionArn on the cloud stack and re-run `pulumi up`.',
            )
            startPolling()
            return
          }
          await sleep([1000, 2000, 5000][Math.min(failures - 1, 2)])
        }
      }
    }

    if (this.streamBase && !this.streamGiveUp) void runStream()
    else startPolling()

    return () => {
      stopped = true
      controller.abort()
      if (pollTimer !== null) {
        clearInterval(pollTimer)
        pollTimer = null
      }
    }
  }

  getBattery(): Promise<Battery> {
    return this.req('GET', '/battery')
  }

  getBatteryPacks(): Promise<BatteryPack[]> {
    return this.req('GET', '/battery-packs')
  }

  getTelemetry(): Promise<TelemetryStatus> {
    return this.req('GET', '/telemetry')
  }

  setBackupReserve(pct: number): Promise<Battery> {
    return this.req('PUT', '/battery/backup-reserve', { pct: Math.round(pct) })
  }

  setSocLimits(ceilingPct: number, floorPct: number): Promise<Battery> {
    return this.req('PUT', '/battery/soc-limits', {
      ceilingPct: Math.round(ceilingPct),
      floorPct: Math.round(floorPct),
    })
  }

  getAuthority(): Promise<Authority> {
    return this.req('GET', '/authority')
  }

  getCommands(): Promise<CommandsStatus> {
    return this.req('GET', '/commands')
  }

  getGridJurisdiction(): Promise<GridJurisdiction> {
    return this.req('GET', '/grid/jurisdiction')
  }

  getDerStatus(): Promise<DerStatusStatus> {
    return this.req('GET', '/grid/derstatus')
  }

  triggerBackstop(
    action: 'start' | 'stop',
    opts?: { jurisdiction?: string; limitW?: number; durationMinutes?: number },
  ): Promise<BackstopStatus> {
    return this.req('POST', '/grid/backstop', { action, ...opts })
  }

  getModes(): Promise<Mode[]> {
    return this.req('GET', '/modes')
  }

  setModeEnabled(id: string, enabled: boolean): Promise<Mode[]> {
    return this.req('PUT', `/modes/${encodeURIComponent(id)}/enabled`, { enabled })
  }

  getBehaviour(): Promise<Behaviour> {
    return this.req('GET', '/behaviour')
  }

  // ---- Stage 4.4: device-command lifecycle + the account's economic profile ----

  submitDeviceCommand(request: DeviceCommandRequest): Promise<DeviceCommandSubmission> {
    // The idempotency key is minted HERE when the caller did not supply one, so a
    // retried fetch (offline flap, 502 from the edge) re-sends the SAME id and the
    // cloud answers with the first record instead of issuing a second command.
    return this.req('POST', '/device/commands', {
      commandId: request.commandId ?? crypto.randomUUID(),
      deviceId: request.deviceId,
      control: request.control,
      value: request.value,
      ...(request.reason ? { reason: request.reason } : {}),
      ...(request.ttlMs ? { ttlMs: request.ttlMs } : {}),
      ...(request.expectedRevision != null ? { expectedRevision: request.expectedRevision } : {}),
    })
  }

  getDeviceCommands(deviceId?: string): Promise<CommandView[]> {
    return this.req<{ commands: CommandView[] }>(
      'GET',
      `/device/commands${deviceId ? `?deviceId=${encodeURIComponent(deviceId)}` : ''}`,
    ).then((r) => r.commands)
  }

  getEconomicSettings(): Promise<EconomicSettings> {
    return this.req('GET', '/economic-settings')
  }

  updateEconomicSettings(patch: EconomicSettingsPatch): Promise<EconomicSettings> {
    return this.req('PUT', '/economic-settings', patch)
  }

  setBehaviour(flag: keyof Behaviour, enabled: boolean): Promise<Behaviour> {
    return this.req('PUT', `/behaviour/${encodeURIComponent(flag)}`, { enabled })
  }

  getSchedule(): Promise<TouSlot[]> {
    return this.req('GET', '/schedule')
  }

  setSchedule(slots: TouSlot[]): Promise<TouSlot[]> {
    return this.req('PUT', '/schedule', { slots })
  }

  getTariff(): Promise<Tariff> {
    return this.req('GET', '/tariff')
  }

  setTariffType(type: TariffType): Promise<Tariff> {
    return this.req('PUT', '/tariff/type', { type })
  }

  setTariffPeriods(periods: TariffPeriod[]): Promise<Tariff> {
    return this.req('PUT', '/tariff/periods', { periods })
  }

  setFlatRate(rate: number): Promise<Tariff> {
    return this.req('PUT', '/tariff/flat-rate', { rate })
  }

  setTariffTiers(tiers: TariffTier[]): Promise<Tariff> {
    return this.req('PUT', '/tariff/tiers', { tiers })
  }

  setDemandCharge(demand: DemandCharge): Promise<Tariff> {
    return this.req('PUT', '/tariff/demand', { demand })
  }

  getWeather(): Promise<Weather> {
    return this.req('GET', '/weather')
  }

  getEnergy(period: EnergyPeriod): Promise<EnergySummary> {
    return this.req('GET', `/energy?period=${encodeURIComponent(period)}`)
  }

  getRecordedEnergyDay(date: string): Promise<RecordedEnergyDay> {
    return this.req('GET', `/energy/recorded-day?date=${encodeURIComponent(date)}`)
  }

  getNotifications(): Promise<AppNotification[]> {
    return this.req('GET', '/notifications')
  }

  markNotificationsRead(ids?: string[]): Promise<AppNotification[]> {
    return this.req('PUT', '/notifications/read', ids ? { ids } : {})
  }

  getNotificationPrefs(): Promise<NotificationPrefs> {
    return this.req('GET', '/notification-prefs')
  }

  setNotificationPrefs(prefs: NotificationPrefs): Promise<NotificationPrefs> {
    return this.req('PUT', '/notification-prefs', { prefs })
  }

  getConsents(): Promise<Consent[]> {
    return this.req('GET', '/consents')
  }

  setConsent(id: string, status: 'active' | 'inactive'): Promise<Consent[]> {
    return this.req('PUT', `/consents/${encodeURIComponent(id)}`, { status })
  }

  // ---- Phase-12: VPP dispatch (ICD-API-02) ----
  // The partner API takes a SEPARATE bearer (the OAuth2 client-credentials grant) —
  // the cloud refuses a homeowner session on /partner/* and refuses a partner token
  // everywhere else. So these five methods must NOT go through `req`, which attaches
  // the session token; `partnerReq` attaches the partner token instead and mints one
  // on demand. Keeping the two bearers apart in the CLIENT is what lets the server's
  // audience separation actually be exercised (rather than papered over).

  getVppStatus(): Promise<VppSiteStatus> {
    return this.req('GET', '/vpp') // homeowner-side — the SESSION token
  }

  async vppPartnerToken(clientId: string, clientSecret: string): Promise<VppTokenResponse> {
    // Minted with the SESSION bearer (the demo binds the grant to the caller's own
    // site as the one real member of the simulated fleet), then cached.
    const res = await this.req<VppTokenResponse>('POST', '/partner/token', { clientId, clientSecret })
    this.partnerToken = res.accessToken
    return res
  }

  /** The cached partner bearer, minting one with the demo credentials if absent. */
  private async ensurePartner(): Promise<string> {
    if (!this.partnerToken) await this.vppPartnerToken(PARTNER_CLIENT_ID, PARTNER_CLIENT_SECRET)
    return this.partnerToken!
  }

  /** A request on the PARTNER API, carrying the partner bearer. A 401/403 drops the
   *  cached grant and retries once with a fresh one (the grant expires like any token). */
  private async partnerReq<T>(method: string, path: string, body?: unknown): Promise<T> {
    let token = await this.ensurePartner()
    const send = () =>
      fetchWithTimeout(`${this.base}${path}`, {
        method,
        headers: {
          authorization: `Bearer ${token}`,
          ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
      })
    let res = await send()
    if (res.status === 401 || res.status === 403) {
      this.partnerToken = null
      token = await this.ensurePartner()
      res = await send()
    }
    if (!res.ok) throw await toError(res)
    return (await res.json()) as T
  }

  vppPartnerFleet(): Promise<VppPartnerView> {
    return this.partnerReq('GET', '/partner/fleet')
  }

  vppDispatch(req: VppDispatchRequest): Promise<VppEvent> {
    return this.partnerReq('POST', '/partner/dispatch', req)
  }

  async vppPartnerEvents(): Promise<VppEvent[]> {
    return (await this.partnerReq<{ events: VppEvent[] }>('GET', '/partner/events')).events
  }

  async vppPartnerWebhooks(): Promise<VppWebhookDelivery[]> {
    return (await this.partnerReq<{ webhooks: VppWebhookDelivery[] }>('GET', '/partner/webhooks')).webhooks
  }

  getUiPrefs(): Promise<UiPrefs> {
    return this.req('GET', '/ui-prefs')
  }

  setUiPrefs(prefs: UiPrefs): Promise<UiPrefs> {
    return this.req('PUT', '/ui-prefs', { prefs })
  }

  getAdm(): Promise<Adm> {
    return this.req('GET', '/adm')
  }

  requestAdmReview(decisionId: string): Promise<Adm> {
    return this.req('POST', '/adm/review', { decisionId })
  }

  getInverter(): Promise<Inverter> {
    return this.req('GET', '/inverter')
  }

  getDevices(): Promise<Device[]> {
    return this.req('GET', '/devices')
  }

  pairDevice(kind: DeviceKind, site?: string): Promise<Device[]> {
    return this.req('POST', '/devices/pair', site ? { kind, site } : { kind })
  }

  getPairing(deviceId?: string): Promise<PairingStatus> { return this.req('GET', `/pairing${deviceId ? `?deviceId=${encodeURIComponent(deviceId)}` : ''}`) }
  recordLocalPairing(action: 'pair' | 'unpair', expectedRevision: number, deviceId: string, residenceId?: string): Promise<PairingStatus> {
    return this.req('POST', '/pairing/events', { action, expectedRevision, deviceId, residenceId, eventId: crypto.randomUUID() })
  }
  remoteUnpairDevice(expectedRevision: number, deviceId?: string): Promise<PairingStatus> {
    return this.req('POST', '/pairing/remote-unpair', { expectedRevision, deviceId, commandId: crypto.randomUUID() })
  }
  cancelRemoteUnpair(deviceId?: string): Promise<PairingStatus> {
    return this.req('POST', '/pairing/remote-unpair/cancel', { deviceId })
  }
  async getHelpFaq(): Promise<import('./types').HelpFaqItem[]> {
    // Public route (APP-ACC-06) — no bearer required.
    const res = await fetchWithTimeout(`${this.base}/help/faq`, { method: 'GET' })
    if (!res.ok) throw await toError(res)
    const data = await res.json() as { items?: import('./types').HelpFaqItem[] } | import('./types').HelpFaqItem[]
    return Array.isArray(data) ? data : (data.items ?? [])
  }
  listSupportEnquiries(): Promise<import('./types').SupportEnquiry[]> {
    return this.req('GET', '/support-enquiries')
  }
  createSupportEnquiry(subject: string, attachDiagnostics: boolean): Promise<import('./types').SupportEnquiry> {
    return this.req('POST', '/support-enquiries', { subject, attachDiagnostics })
  }
  postSupportMessage(enquiryId: string, body: string): Promise<import('./types').SupportEnquiry> {
    return this.req('POST', '/support-enquiries/message', { enquiryId, body })
  }
  requestSupportCallback(enquiryId: string, phone?: string): Promise<import('./types').SupportEnquiry> {
    return this.req('POST', '/support-enquiries/callback', { enquiryId, phone })
  }
  forceDetachPairing(deviceId: string, expectedRevision: number, caseId: string, reason: string): Promise<PairingStatus> {
    return this.req('POST', '/pairing/force-detach', { deviceId, expectedRevision, caseId, reason, commandId: crypto.randomUUID() })
  }
  manageHouseholdMember(action: 'invite' | 'remove', email: string, access?: 'Family' | 'Viewer', deviceId?: string): Promise<PairingStatus> {
    return this.req('POST', `/pairing/members${deviceId ? `?deviceId=${encodeURIComponent(deviceId)}` : ''}`, { action, email, access })
  }

  getCommissioning(site?: string): Promise<CommissioningStep[]> {
    return this.req('GET', site ? `/installer/commissioning?site=${encodeURIComponent(site)}` : '/installer/commissioning')
  }

  advanceCommissioning(site?: string): Promise<CommissioningStep[]> {
    return this.req('POST', site ? `/installer/commissioning/advance?site=${encodeURIComponent(site)}` : '/installer/commissioning/advance', {})
  }

  getDiagnostics(site?: string): Promise<DiagnosticRegister[]> {
    return this.req('GET', site ? `/installer/diagnostics?site=${encodeURIComponent(site)}` : '/installer/diagnostics')
  }

  getCompliance(): Promise<ComplianceRecord> {
    return this.req('GET', '/installer/compliance')
  }

  getFirmware(): Promise<FirmwareInfo[]> {
    return this.req('GET', '/installer/firmware')
  }

  applyFirmwareUpdate(component: string): Promise<FirmwareInfo[]> {
    return this.req('POST', '/installer/firmware/update', { component })
  }

  // ---- firmware update lifecycle + region profile (phase 4.5) ----
  getUpdates(site?: string): Promise<UpdatePlan> {
    // `?site=` addresses a Residence the caller is a member of; the server runs
    // `requireMembership` on it (cloud/src/handler.ts `GET /updates`). Sending it
    // is the whole point of the parameter — an installer screen that dropped it
    // silently read the installer's own site instead.
    return this.req('GET', site ? `/updates?site=${encodeURIComponent(site)}` : '/updates')
  }

  startUpdate(jobId: string): Promise<UpdateJobView> {
    // The jobId travels in the BODY, not the path: this API's routes are exact
    // matches on the server (`cloud/src/handler.ts` switches on
    // `<METHOD> <path>`), and a path parameter would be a route shape nothing
    // else here uses.
    return this.req<{ update: UpdateJobView }>('POST', '/updates/start', { jobId }).then((r) => r.update)
  }

  retryUpdate(jobId: string): Promise<UpdateJobView> {
    return this.req<{ update: UpdateJobView }>('POST', '/updates/retry', { jobId }).then((r) => r.update)
  }

  getFirmwareCampaigns(): Promise<FirmwareCampaignView[]> {
    return this.req<{ campaigns: FirmwareCampaignView[] }>('GET', '/installer/firmware/campaigns').then((r) => r.campaigns)
  }

  // ---- Stopping a rollout (CLD-OTA-02) ----
  // TWO ROUTES, ONE BEHAVIOUR, and the path is written literally at each entry
  // point — never assembled from a caller-supplied scope. The server derives the
  // acting staff role from the path, so a `scope` argument would turn the record of
  // who stopped a fleet-wide rollout into a claim the client made about itself.
  // (Same rule as the serial re-assignment pair below.)

  /** The wire answer is `{ campaign, aborted, refused }`, where `campaign` is the
   *  campaign RECORD (status/abortedAtMs/abortReason) rather than the aggregate
   *  `getFirmwareCampaigns` returns — the two are different shapes and are not
   *  interchangeable, so the fields the caller needs are lifted out here rather
   *  than the record being passed off as a `FirmwareCampaignView`. `aborted` is
   *  already a list of job ids on the wire, and `refused` is carried through
   *  untouched: dropping it would hide the half of the outcome that still needs a
   *  person. */
  private abortCampaignAt(path: string, campaignId: string, reason: string): Promise<FirmwareCampaignAbort> {
    return this.req<{
      campaign: { campaignId: string; status: FirmwareCampaignView['status']; abortedAtMs: number | null; abortReason: string | null }
      aborted: string[]
      refused: { jobId: string; reason: string }[]
    }>('POST', path, { campaignId, reason }).then((r) => ({
      campaignId: r.campaign.campaignId,
      status: r.campaign.status,
      abortedAtMs: r.campaign.abortedAtMs,
      abortReason: r.campaign.abortReason,
      aborted: r.aborted,
      refused: r.refused,
    }))
  }

  adminAbortFirmwareCampaign(campaignId: string, reason: string): Promise<FirmwareCampaignAbort> {
    return this.abortCampaignAt('/admin/firmware/campaigns/abort', campaignId, reason)
  }

  supportAbortFirmwareCampaign(campaignId: string, reason: string): Promise<FirmwareCampaignAbort> {
    return this.abortCampaignAt('/support/firmware/campaigns/abort', campaignId, reason)
  }

  getFleet(): Promise<FleetSite[]> {
    return this.req('GET', '/installer/fleet')
  }

  createResidence(label: string, address: string, nmi: string): Promise<FleetSite[]> {
    return this.req('POST', '/installer/residences', { label, address, nmi })
  }

  /** Queues the installer's own device-confirmed unpair; `pending` until the dongle
   *  confirms it. `ownerEmail` is optional contact information (APP-ACC-03). */
  handoverResidence(site: string, ownerEmail: string): Promise<HandoverResult> {
    return this.req('POST', '/installer/handover', { site, ownerEmail })
  }

  unpairResidence(site: string): Promise<FleetSite[]> {
    return this.req('POST', '/installer/unpair', { site })
  }

  // ---- Installer Organisation, practitioners and inventory (APP-ACC-08–08L) ----

  /** The organisation to act for: the caller's explicit argument, else the stored
   *  client choice, else nothing — in which case the SERVER decides, and refuses
   *  (400) when the account belongs to more than one (APP-ACC-08L). The client
   *  never picks one on the caller's behalf. */
  private actingOrg(explicit?: string): string | undefined {
    return explicit ?? localStorage.getItem(ACTING_ORG_KEY) ?? undefined
  }

  private orgQuery(explicit?: string): string {
    const org = this.actingOrg(explicit)
    return org ? `?organisationId=${encodeURIComponent(org)}` : ''
  }

  /** The organisation field every organisation-scoped POST body carries. */
  private orgBody(explicit?: string): { organisationId?: string } {
    const org = this.actingOrg(explicit)
    return org ? { organisationId: org } : {}
  }

  getInstallerOrganisation(organisationId?: string): Promise<InstallerOrganisationView> {
    return this.req('GET', `/installer/organisation${this.orgQuery(organisationId)}`)
  }

  linkPractitioner(userId: string, role: OrgRole, organisationId?: string): Promise<OrgMembership> {
    return this.req('POST', '/installer/organisation/practitioners', { ...this.orgBody(organisationId), userId, role })
  }

  acceptOrgInvitation(organisationId: string): Promise<OrgMembership> {
    return this.req('POST', '/installer/organisation/practitioners/accept', { organisationId })
  }

  revokePractitioner(userId: string, reason: string, organisationId?: string): Promise<{ revoked: boolean; membership: OrgMembership | null }> {
    return this.req('POST', '/installer/organisation/practitioners/revoke', { ...this.orgBody(organisationId), userId, reason })
  }

  getOrganisationAudit(organisationId?: string): Promise<OrgAuditEvent[]> {
    return this.req('GET', `/installer/organisation/audit${this.orgQuery(organisationId)}`)
  }

  async getInventory(organisationId?: string): Promise<InventoryUnit[]> {
    const r = await this.req<{ units: InventoryUnit[] }>('GET', `/installer/inventory${this.orgQuery(organisationId)}`)
    return r.units ?? []
  }

  declareInventory(serials: string[], kind?: InventoryKind, organisationId?: string): Promise<DeclarationResult> {
    return this.req('POST', '/installer/inventory/declare', { ...this.orgBody(organisationId), serials, ...(kind ? { kind } : {}) })
  }

  withdrawInventory(serial: string, reason: string, organisationId?: string): Promise<InventoryUnit> {
    return this.req('POST', '/installer/inventory/withdraw', { ...this.orgBody(organisationId), serial, reason })
  }

  correctInventory(fromSerial: string, toSerials: string[], kind?: InventoryKind, organisationId?: string): Promise<CorrectionResult> {
    return this.req('POST', '/installer/inventory/correct', { ...this.orgBody(organisationId), fromSerial, toSerials, ...(kind ? { kind } : {}) })
  }

  validateInventorySerial(serial: string, organisationId?: string): Promise<InventoryValidation> {
    return this.req('POST', '/installer/inventory/validate', { ...this.orgBody(organisationId), serial })
  }

  // ---- Staff serial re-assignment (SYS-FUN-17J1 / CLD-PRT-01E1) ----
  // TWO ROUTES, ONE BEHAVIOUR. The acting scope is the ROUTE, never a body field:
  // the server derives it from the path and stamps it on the audit event, so the
  // record of who acted cannot be a claim the client made about itself. Neither
  // carries `organisationId` from `orgBody` — the organisation named here is the
  // one the serial moves TO, and staff are not members of it.
  adminReassignInventorySerial(serial: string, organisationId: string, reason: string): Promise<InventoryUnit> {
    return this.req('POST', '/admin/inventory/reassign', { serial, organisationId, reason })
  }

  supportReassignInventorySerial(serial: string, organisationId: string, reason: string): Promise<InventoryUnit> {
    return this.req('POST', '/support/inventory/reassign', { serial, organisationId, reason })
  }

  removeFleetSite(site: string, organisationId?: string): Promise<FleetSite[]> {
    return this.req('POST', '/installer/fleet/remove', { ...this.orgBody(organisationId), site })
  }

  // ---- Homeowner view of retained associations (APP-OWN-18/18A) ----

  async getFleetAssociations(): Promise<FleetAssociation[]> {
    const r = await this.req<{ associations: FleetAssociation[] }>('GET', '/fleet-associations')
    return r.associations ?? []
  }

  async removeFleetAssociation(organisationId: string): Promise<FleetAssociation[]> {
    await this.req('POST', '/fleet-associations/remove', { organisationId })
    // The removal returns the ended association; the homeowner's list is the
    // remaining ACTIVE ones, so re-read rather than mutate a local copy.
    return this.getFleetAssociations()
  }

  // ---- Acting-organisation selection (APP-ACC-08L) ----

  async getActingOrganisation(): Promise<string | null> {
    return localStorage.getItem(ACTING_ORG_KEY)
  }

  async setActingOrganisation(organisationId: string | null): Promise<void> {
    if (organisationId) localStorage.setItem(ACTING_ORG_KEY, organisationId)
    else localStorage.removeItem(ACTING_ORG_KEY)
  }


  // ---- Consent log, warranty and HWS ----

  getConsentLog(): Promise<ConsentLogEntry[]> {
    return this.req('GET', '/consent-log')
  }

  getWarranty(): Promise<Warranty> {
    return this.req('GET', '/warranty')
  }


  async getHwsDeclaration(site?: string): Promise<HwsDeclaration | null> {
    const r = await this.req<{ hws: HwsDeclaration | null }>(
      'GET',
      site ? `/installer/commissioning/hws?site=${encodeURIComponent(site)}` : '/installer/commissioning/hws',
    )
    return r.hws ?? null
  }

  setHwsDeclaration(relayConnected: boolean, hotByHour: number | undefined, site?: string): Promise<HwsDeclaration> {
    return this.req(
      'POST',
      site ? `/installer/commissioning/hws?site=${encodeURIComponent(site)}` : '/installer/commissioning/hws',
      { relayConnected, hotByHour },
    )
  }

  // ---- In-app documents and support role ----

  listDocuments(model?: string): Promise<DocMeta[]> {
    return this.req('GET', model ? `/documents?model=${encodeURIComponent(model)}` : '/documents')
  }

  getDocument(id: string, version?: string): Promise<DocContent> {
    return this.req('GET', version
      ? `/documents/${encodeURIComponent(id)}?version=${encodeURIComponent(version)}`
      : `/documents/${encodeURIComponent(id)}`)
  }

  supportOverview(): Promise<SupportOverview> {
    return this.req('GET', '/support/site')
  }

  // ---- Stage 4.3: the onsite BLE surface (APP-INS-08) ----

  /**
   * The BLE-contract client, over the vertical slice's DEV bridge.
   *
   * READ `createHttpBridgeTransport`'s note before touching this. The bridge is
   * scaffolding: a browser has no BLE radio, so until the native adapter of
   * phase 5.3 exists the only way to run this app against the REAL Simulated
   * Dongle — the one that does REAL Modbus against the Simulated Inverter — is
   * to tunnel opaque envelope frames to the dev server that composes it. The
   * `/__dev/` prefix is the dev server's own reserved namespace; the deployed
   * cloud has no such route and must never gain one, because a WAN route into a
   * dongle's LOCAL BLE listener would contradict invariant 3 and DNG-COM-01
   * outright. Pointed at a real cloud this 404s, and the client reports the link
   * as unavailable instead of pretending it worked.
   */
  ble(): BleClient {
    return this.bleClient ?? (this.bleClient = createBleClient(createHttpBridgeTransport({ baseUrl: this.base })))
  }

}

async function toError(res: Response): Promise<Error> {
  let detail = res.statusText
  try {
    const body = await res.json()
    if (body?.message) detail = body.message
    else if (body?.error) detail = body.error
  } catch {
    /* non-JSON body */
  }
  const e = new Error(`${res.status} ${detail}`) as Error & { status: number }
  e.status = res.status
  return e
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/** Split a growing SSE receive buffer into complete frames + the unconsumed tail.
 *  Mirror of cloud/src/sse.ts `parseSseChunk`; keep the two in sync. */
function parseSseChunk(buffer: string): { frames: unknown[]; rest: string } {
  const frames: unknown[] = []
  let idx: number
  while ((idx = buffer.indexOf('\n\n')) >= 0) {
    const block = buffer.slice(0, idx)
    buffer = buffer.slice(idx + 2)
    const data = block
      .split('\n')
      .filter((l) => l.startsWith('data:'))
      .map((l) => l.slice(5).trim())
      .join('\n')
    if (data) {
      try {
        frames.push(JSON.parse(data))
      } catch {
        /* skip an unparseable payload */
      }
    }
  }
  return { frames, rest: buffer }
}
